import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { spApi } from '@/lib/api-client';
import { useLocationStore } from '@/stores/location-store';
import { invalidateVendorQueries } from './use-payees';

// ── Types ──

export interface SPPayee {
  id: string;
  tenantId: string;
  name: string;
  email: string | null;
  contactName: string | null;
  phone: string | null;
  company: string | null;
  bankName: string | null;
  accountHolderName: string | null;
  accountBusinessType: string | null;
  accountType: string | null;
  routingNumber: string | null;
  accountNumber: string | null;
  isActive: boolean;
  paymentCount?: number;
  createdAt: string;
}

export interface SPFundingAccount {
  id: string;
  // sendpayment requires locationId on create + immutable on update; the
  // server returns it on every fetch so it's part of the canonical shape.
  locationId: string;
  nickname: string;
  bankName: string;
  accountType: string;
  routingNumber: string;
  accountNumber: string;
  accountHolderName: string;
  accountBusinessType?: string;
  isPrimary: boolean;
  isActive: boolean;
}

export interface SPPayment {
  id: string;
  referenceNumber: string;
  // Location the payment is scoped to. Set on create from the active
  // location store; null for legacy rows that pre-date location-scoping.
  locationId: string | null;
  payeeId: string;
  fundingAccountId: string;
  amount: string;
  currency: string;
  memo: string | null;
  statementDescriptor: string | null;
  paymentMethod: string;
  status: string;
  statusMessage: string | null;
  createdAt: string;
  processedAt: string | null;
  completedAt: string | null;
  failedAt: string | null;
  payee: { name: string | null; company: string | null; bankName: string | null };
  fundingAccount: { nickname: string | null; bankName: string | null };
}

export interface SPStatusHistory {
  id: string;
  paymentId: string;
  fromStatus: string | null;
  toStatus: string;
  message: string | null;
  createdAt: string;
}

export interface SPDashboardData {
  payees: { total: number; active: number };
  payments: {
    total: number;
    pending: number;
    completed: number;
    failed: number;
    totalAmount: string;
    completedAmount: string;
    byStatus: { status: string; count: number; amount: string }[];
    byMethod: { method: string; count: number; amount: string }[];
  };
  topVendors: {
    payeeId: string;
    name: string | null;
    company: string | null;
    totalPaid: string;
    paymentCount: number;
  }[];
  purchaseOrders: { total: number; totalValue: string };
  recentPayments: (SPPayment & { payee: { name: string | null; company: string | null } })[];
}

// ── Queries ──

export function useSPPayees(
  params?: { search?: string; status?: string; locationId?: string },
  // `enabled: false` skips the request for a viewer without Payees.
  options?: { enabled?: boolean },
) {
  // Auto-scope to the active location unless caller explicitly opted out
  // (passed locationId, even an empty string, takes precedence).
  const selectedLocationId = useLocationStore((s) => s.selectedLocationId);
  const scopedParams =
    params?.locationId === undefined && selectedLocationId
      ? { ...(params ?? {}), locationId: selectedLocationId }
      : params;
  return useQuery({
    queryKey: ['sp', 'payees', scopedParams],
    queryFn: () => spApi.get<{ data: SPPayee[] }>('/payees', scopedParams),
    enabled: options?.enabled ?? true,
  });
}

export function useSPFundingAccounts(params?: { locationId?: string }) {
  // Auto-scope to the active location, same as useSPPayees. Without this the
  // list spans every location and "primary" can resolve to another location's account.
  const selectedLocationId = useLocationStore((s) => s.selectedLocationId);
  const scopedParams =
    params?.locationId === undefined && selectedLocationId
      ? { ...(params ?? {}), locationId: selectedLocationId }
      : params;
  return useQuery({
    queryKey: ['sp', 'funding-accounts', scopedParams],
    queryFn: () => spApi.get<{ data: SPFundingAccount[] }>('/funding-accounts', scopedParams),
  });
}

/**
 * The funding account outgoing payments are locked to: the location's primary
 * account, created from the onboarding bank details. sendpayment rejects any other.
 */
export function useLockedFundingAccount(locationId?: string) {
  const query = useSPFundingAccounts(locationId === undefined ? undefined : { locationId });
  const account = (query.data?.data ?? []).find((a) => a.isActive && a.isPrimary);
  return { account, isLoading: query.isLoading };
}

export function useSPPayments(params?: {
  status?: string;
  payeeId?: string;
  locationId?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['sp', 'payments', params],
    queryFn: () =>
      spApi.get<{ data: SPPayment[]; total: number; limit: number; offset: number }>(
        '/payments',
        params as Record<string, string | number>,
      ),
  });
}

export function useSPPaymentDetail(id: string | null) {
  return useQuery({
    queryKey: ['sp', 'payment', id],
    queryFn: () => spApi.get<{ data: SPPayment & { statusHistory: SPStatusHistory[] } }>(`/payments/${id}`),
    enabled: !!id,
  });
}

export function useSPDashboard(params?: { locationId?: string }) {
  return useQuery({
    queryKey: ['sp', 'dashboard', params],
    queryFn: () => spApi.get<{ data: SPDashboardData }>('/dashboard', params as Record<string, string>),
  });
}

// ── Mutations ──

export function useCreateSPPayee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SPPayee>) => spApi.post<{ data: SPPayee }>('/payees', data),
    onSuccess: () => invalidateVendorQueries(qc),
  });
}

export function useUpdateSPPayee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<SPPayee> & { id: string }) =>
      spApi.put<{ data: SPPayee }>(`/payees/${id}`, data),
    onSuccess: () => invalidateVendorQueries(qc),
  });
}

export function useDeleteSPPayee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => spApi.delete(`/payees/${id}`),
    // Deactivating a vendor has to clear the product and PO pickers too, not
    // just this list — see invalidateVendorQueries.
    onSuccess: () => invalidateVendorQueries(qc),
  });
}

export function useCreateSPFundingAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<SPFundingAccount>) => spApi.post<{ data: SPFundingAccount }>('/funding-accounts', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'funding-accounts'] }),
  });
}

export function useUpdateSPFundingAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<SPFundingAccount> & { id: string }) =>
      spApi.put<{ data: SPFundingAccount }>(`/funding-accounts/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'funding-accounts'] }),
  });
}

export function useDeleteSPFundingAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => spApi.delete(`/funding-accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'funding-accounts'] }),
  });
}

export function useSetPrimaryFundingAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => spApi.put<{ data: SPFundingAccount }>(`/funding-accounts/${id}/set-primary`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sp', 'funding-accounts'] }),
  });
}

export function useCreateSPPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      payeeId: string;
      fundingAccountId: string;
      amount: number;
      // Required so the payment is visible to the location-scoped /payments
      // listing — caller passes the active location from the store.
      locationId?: string;
      // Merchant ID (externalId of the selected location) — used by the
      // backend to look up the active ACH gateway config for this merchant.
      merchantId?: string;
      statementDescriptor?: string;
      memo?: string;
      paymentMethod?: string;
    }) => spApi.post<{ data: SPPayment }>('/payments', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'payments'] });
      qc.invalidateQueries({ queryKey: ['sp', 'dashboard'] });
    },
  });
}

export function useEditSPPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      payeeId?: string;
      fundingAccountId?: string;
      amount?: number;
      memo?: string;
      paymentMethod?: string;
    }) => spApi.put<{ data: SPPayment }>(`/payments/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'payments'] });
      qc.invalidateQueries({ queryKey: ['sp', 'dashboard'] });
    },
  });
}

export function useUpdateSPPaymentStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, message }: { id: string; status: string; message?: string }) =>
      spApi.put<{ data: SPPayment }>(`/payments/${id}/status`, { status, message }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sp', 'payments'] });
      qc.invalidateQueries({ queryKey: ['sp', 'payment'] });
      qc.invalidateQueries({ queryKey: ['sp', 'dashboard'] });
    },
  });
}

// ── NACHA ──

export interface SPNachFileRecord {
  record: {
    id: string;
    nachFileId: string;
    sequenceIndex: string;
    indicator: number;
    batchNumber: number | null;
    payeeId: string | null;
    payeeName: string | null;
    paymentId: string | null;
    routingNumber: string | null;
    accountNumber: string | null;
    transactionAmount: string | null;
    transactionCode: number | null;
    addenda: string | null;
    createdAt: string;
  };
  fileSequence: string | null;
  startDate: string | null;
  endDate: string | null;
}

export function useSPNachFileRecords(params?: { nachFileId?: string; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['sp', 'nach-file-records', params],
    queryFn: () =>
      spApi.get<{ data: SPNachFileRecord[] }>('/nach/file-records', params as Record<string, string | number>),
  });
}
